import React, {
  useEffect, useState, useCallback, Dispatch, SetStateAction,
} from 'react';
import { useAppSelector } from 'store';
import {
  Modal, Button, Card, Accordion, Icon,
} from 'semantic-ui-react';
import {
  Other,
} from 'simplydo/interfaces';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from 'api';
import util from 'utils/utils';

type IProjectBoardSetup = {
  forId: string,
  lanes: Other.IProjectLane[],
  dataLoaded?: boolean,
  setLanes: Dispatch<SetStateAction<Other.IProjectLane[]>>,
  setupModalOpen: boolean,
  setSetupModalOpen: Dispatch<SetStateAction<boolean>>,
}

const ProjectBoardSetup = ({
  forId, lanes = [], dataLoaded, setLanes, setupModalOpen, setSetupModalOpen,
}: IProjectBoardSetup) => {
  // We use this flag to ensure the modal doesn't reopen once it's been closed
  const [setupHandled, setSetupHandled] = useState<boolean>(false);
  const [myChallenges, setMyChallenges] = useState<Other.IChallenge[]>([]);
  const [importing, setImporting] = useState<boolean>(false);
  const [selectedChallengeId, setSelectedChallengeId] = useState<string>('');
  const [accordionOpen, setAccordionOpen] = useState<boolean>(false);
  const user = useAppSelector<Other.IUserMe>((state) => state.user);
  const userId = user?._id;

  const getChallenges = useCallback(() => {
    api.users.getChallenges(userId, { withProjectBoard: true, query: '' }, (data) => {
      setMyChallenges(data.challenges);
    });
  }, [userId]);

  const importLanes = useCallback(() => {
    setImporting(true);
    api.boards.importProjectLanes(forId, { fromBoardId: selectedChallengeId }, (data) => {
      if (data.success) {
        setLanes(data.lanes);
        toast.success('Lanes imported successfully.');
        setImporting(false);
        setSetupModalOpen(false);
      } else {
        toast.error(data.message);
      }
      setImporting(false);
    }, (err) => {
      setImporting(false);
      toast.error(err.message);
    });
  }, [forId, selectedChallengeId, setLanes, setSetupModalOpen]);

  useEffect(() => {
    getChallenges();
  }, [getChallenges]);

  useEffect(() => {
    if (!lanes.length && dataLoaded && !setupHandled) {
      setSetupModalOpen(true);
      setSetupHandled(true);
    }
  }, [lanes, dataLoaded, setSetupModalOpen, setupHandled]);

  return (
    <Modal
      open={setupModalOpen}
      onClose={() => setSetupModalOpen(false)}
    >
      <Modal.Header>
        Welcome to the idea board
      </Modal.Header>
      <Modal.Content>
        <p>
          This idea board is not setup yet. You may import the lane structure from another challenge you manage, or start from scratch.
        </p>
        <Accordion>
          <Accordion.Title active={accordionOpen} onClick={() => setAccordionOpen((prevOpen) => !prevOpen)}>
            <Icon name='dropdown' />
            Select a challenge to import from
          </Accordion.Title>
          <Accordion.Content active={accordionOpen}>
            <Card.Group itemsPerRow={4} centered>
              {myChallenges.map((c) => (
                <Card raised key={c._id} onClick={() => setSelectedChallengeId((prevSelected) => (prevSelected === c._id ? '' : c._id))}>
                  <Button
                    icon='check'
                    color={selectedChallengeId === c._id ? 'violet' : null}
                    style={{
                      position: 'absolute', top: 5, left: 5, width: 50,
                    }}
                  />
                  <div style={{
                    height: 150, backgroundPosition: 'center center', backgroundSize: 'cover', backgroundImage: `url(${util.challengeImage(c)})`,
                  }} />
                  <Card.Content>
                    <Card.Header>{c.name}</Card.Header>
                    <Card.Meta>Copy the lane structure from this challenge</Card.Meta>
                    <Card.Meta style={{ marginTop: 10 }}>
                      <Link className='with-border' style={{ textDecorationLine: 'underline' }} to={`/challenges/${c._id}/board`} target="_blank">View idea board</Link>
                    </Card.Meta>
                  </Card.Content>
                </Card>
              ))}
            </Card.Group>
          </Accordion.Content>
        </Accordion>
      </Modal.Content>
      <Modal.Actions>
        {selectedChallengeId
          ? (
            <Button
              icon='cloud download'
              content='Import from selected challenge'
              color='green'
              loading={importing}
              onClick={() => importLanes()}
            />
          ) : (
            <Button
              icon='plus'
              content='Start from scratch'
              onClick={() => setSetupModalOpen(false)}
            />
          )
        }

      </Modal.Actions>
    </Modal>
  );
};

export default ProjectBoardSetup;
